/**
 * BureauStatusBadge — one bureau's credential status (sheet 41 callout 3,
 * sheet 31 callout 5, sheets 11 and 40; mission 7 WP08 T044; FR-005, FR-022;
 * TX.2, TX.9; contracts/ui-api.md "Spine, part 2").
 *
 * The §2.6 tones through the §7.4 `Badge`: Verified is success; Entered, never
 * tested is warning; Auth failed and Disabled by Trellis are error; Not entered
 * is neutral. No status is a running state, so none has a dot. Verified names
 * its date when `verifiedAt` is given, inside `<time dateTime>` (FR-028).
 */
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import type { CredentialStatus } from '@/lib/api/bureaus'
import { date, isoDateTime } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

type Tone = 'success' | 'warning' | 'error' | 'neutral'

const TONE: Record<CredentialStatus, Tone> = {
  verified: 'success',
  entered_untested: 'warning',
  auth_failed: 'error',
  disabled_by_trellis: 'error',
  not_entered: 'neutral',
}

const LABEL: Record<Exclude<CredentialStatus, 'verified'>, string> = {
  entered_untested: copyText('credStatus.enteredUntested'),
  auth_failed: copyText('credStatus.authFailed'),
  disabled_by_trellis: copyText('credStatus.disabledByTrellis'),
  not_entered: copyText('notEntered'),
}

export interface BureauStatusBadgeProps {
  status: CredentialStatus
  /** The last successful test (`last_tested_at`); shown only on Verified. */
  verifiedAt?: string | null | undefined
}

export function BureauStatusBadge({ status, verifiedAt }: BureauStatusBadgeProps) {
  let label: ReactNode
  if (status !== 'verified') label = LABEL[status]
  else if (verifiedAt) {
    label = (
      <time dateTime={isoDateTime(verifiedAt)}>
        {PROVISIONAL_COPY['credStatus.verified'].render({ date: date(verifiedAt) })}
      </time>
    )
  } else label = PROVISIONAL_COPY['credStatus.verified'].render({})

  return (
    <Badge status={TONE[status]} data-credential-status={status}>
      {label}
    </Badge>
  )
}
